import apiClientV2 from '@/api/clientV2';
import type {
  NiboIntegration,
  CostCenter,
  FinancialAccount,
  Category,
  Supplier,
  CreateSupplierInput,
  NiboEntryData,
  NiboEntryDataInput,
  SyncJobResponse,
} from './nibo.types';

export class NiboRepository {
  // === Integration Status ===

  async getIntegration(customerId: string): Promise<NiboIntegration | null> {
    const response = await apiClientV2.get<NiboIntegration | null>(
      `/nibo/integration?customerId=${customerId}`
    );
    return response.data;
  }

  async activate(customerId: string, apiToken: string): Promise<{ success: boolean; message: string; data: NiboIntegration }> {
    const response = await apiClientV2.post<{ success: boolean; message: string; data: NiboIntegration }>(
      '/nibo/integration',
      { customerId, apiToken }
    );
    return response.data;
  }

  async deactivate(integrationId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClientV2.delete<{ success: boolean; message: string }>(
      `/nibo/integration/${integrationId}`
    );
    return response.data;
  }

  // === Master Data ===

  async getCostCenters(customerId: string): Promise<CostCenter[]> {
    const response = await apiClientV2.get<{ data: CostCenter[] }>(
      `/nibo/cost-centers?customerId=${customerId}`
    );
    return response.data.data;
  }

  async getFinancialAccounts(customerId: string): Promise<FinancialAccount[]> {
    const response = await apiClientV2.get<{ data: FinancialAccount[] }>(
      `/nibo/financial-accounts?customerId=${customerId}`
    );
    return response.data.data;
  }

  async getCategories(customerId: string): Promise<Category[]> {
    const response = await apiClientV2.get<{ data: Category[] }>(
      `/nibo/expense-categories?customerId=${customerId}`
    );
    return response.data.data;
  }

  async getSuppliers(customerId: string): Promise<Supplier[]> {
    const response = await apiClientV2.get<{ data: Supplier[] }>(
      `/nibo/suppliers?customerId=${customerId}`
    );
    return response.data.data;
  }

  async createSupplier(input: CreateSupplierInput): Promise<Supplier> {
    const response = await apiClientV2.post<{ data: Supplier }>(
      '/nibo/suppliers',
      input
    );
    return response.data.data;
  }

  // === Entry Data ===

  async getEntryData(entryId: string): Promise<NiboEntryData> {
    const response = await apiClientV2.get<{ data: NiboEntryData }>(
      `/nibo/entries/${entryId}/data`
    );
    return response.data.data;
  }

  async upsertEntryData(
    entryId: string,
    data: NiboEntryDataInput
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClientV2.put<{ success: boolean; message: string }>(
      `/nibo/entries/${entryId}/data`,
      data
    );
    return response.data;
  }

  // === Sync ===

  async syncEntry(entryId: string, customerId: string): Promise<SyncJobResponse> {
    const response = await apiClientV2.post<SyncJobResponse>(
      `/nibo/sync-entry/${entryId}`,
      { customerId }
    );
    return response.data;
  }
}
